import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import axios from "@/utils/Axios";

import { FlexDiv } from "@/components/Atoms/Box";
import StyledSvgSprite from "@/components/Atoms/SVGSprite";
import Button from "@/components/Atoms/Button";
import TextInput from "@/components/Atoms/Inputs/Input";
import { useRegisterForm } from "@/hooks";
import Typography from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";
import { generateUrlArguments } from "@/utils";

import {
  FormCard,
  FormContent,
  FormHeader,
  FormMessage,
  FormTitle,
  HeaderText,
  RightSection,
  RightSectionWrapper,
  ContentText,
  HeaderContentBox,
  Heading,
  InputContainer,
  IconImage,
} from "./style";

function RightSideView() {
  const router = useRouter();
  const urlParams = new URLSearchParams(router.query);

  const [showCallbackModal, setShowCallbackModal] = useState(false);

  const {
    showSuccessModal,
    handleChange,
    onSubmitCallback,
    onShowCallbackModal,
    onHideCallbackModal,
    inputs,
  } = useRegisterForm({
    name: "",
    email: "",
  });

  const onHideNutritionCallbackModal = () => {
    if (showCallbackModal) setShowCallbackModal(false);
    onHideCallbackModal(() => {});
  };

  useEffect(() => {
    if (showSuccessModal === true) {
      setTimeout(onHideNutritionCallbackModal, 6000);
    }
  }, [showSuccessModal]);

  const { name, nameError, email, emailError } = inputs;

  const onSubmit = (e) => {
    const SUBSCRIBE_TO_NEWSLETTER = `${process.env.NEXT_PUBLIC_LOCAL_API_URL}admin/campaign-lead/`;

    axios
      .post(SUBSCRIBE_TO_NEWSLETTER, {
        name,
        email,
        url_arguments: generateUrlArguments(urlParams),
        has_subscribed: true,
      })
      .then((res) => onShowCallbackModal())
      .catch((err) => onShowCallbackModal());
  };

  const onSubmitButtonClick = (state) => {
    onSubmitCallback(true, onSubmit);
  };

  return (
    <RightSection>
      <HeaderContentBox>
        <Heading variant="heading3" padding="0 16px">
          We're excited to announce the launch of our health newsletter.
        </Heading>
        <ContentText variant="paragraph1" fontSize="12px" padding="0 16px">
          We wanted to give you a safe place to get factual information about
          anything and everything to do with health, nutrition, exercise, and so
          much more! Our goal is to establish this newsletter as the go-to
          source for trustworthy and credible health-related information.
        </ContentText>
      </HeaderContentBox>
      <RightSectionWrapper>
        <FormCard>
          <FormHeader>
            <HeaderText variant="heading4">
              Sign up! <br />
              Live healthier
            </HeaderText>
            <IconImage
              src="https://d1mxd7n691o8sz.cloudfront.net/static/mindhouse/imagerepo/2023-07/image_1_2-7ff14865327b4f3cb2dd06aca063ec19.png"
              alt="image"
            />
          </FormHeader>
          {showSuccessModal ? (
            <FormContent>
              <FlexDiv
                alignItems="center"
                flexDirection="column"
                justifyContent="center"
                margin="0"
                id="requestCallbackSuccessModal"
                className="callback-message-wrapper"
                aria-labelledby="requestCallbackSuccessModal"
                padding="32px"
                backgroundColor={theme.colorsV2.success.shade_50}
                width="100%"
                borderRadius="16px"
              >
                <StyledSvgSprite
                  icon="icon-tick-circle"
                  height="66px"
                  width="66px"
                  fill={theme.colors.textGradient.type3static}
                />
                <Typography
                  variant="heading2"
                  textAlign="center"
                  fontSize={["14px", "16px"]}
                  lineHeight="24px"
                  fontFamily="var(--mori)"
                  color={theme.colorsV2.success.shade_900}
                  mt="12px"
                  maxWidth="228px"
                  fontWeight="600"
                  style={{
                    letterSpacing: "0px",
                  }}
                >
                  You have successfully subscribed to our newsletter!
                </Typography>
              </FlexDiv>
            </FormContent>
          ) : (
            <>
              <FormContent>
                <FormTitle variant="heading4">
                  Our health newsletter will empower you with science-backed
                  information on how to lead healthier lives.
                </FormTitle>

                <InputContainer>
                  <TextInput
                    value={name}
                    error={nameError}
                    onChange={handleChange}
                    fontSize="14px"
                    lineHeight="normal"
                    inputPadding="8px 32px"
                    name="name"
                    placeholder="Enter name"
                    required
                    variant="v3"
                    focusable={true}
                    fontFamily="var(--mori)"
                    inputColor="#282828"
                    inputMarginBottom="0px !important"
                    inputWidth="100%"
                    isNotAbsolute
                    inputBorderRadius="47px"
                    style={{
                      border: "1px solid rgba(0,0,0,0.5)",
                      height: "60px",
                    }}
                  />
                  <TextInput
                    value={email}
                    error={emailError}
                    onChange={handleChange}
                    fontSize="16px"
                    lineHeight="normal"
                    inputPadding="8px 32px"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    required
                    variant="v3"
                    focusable={true}
                    fontFamily="var(--mori)"
                    inputColor="#282828"
                    inputMarginBottom="0px !important"
                    inputWidth="100%"
                    isNotAbsolute
                    inputBorderRadius="47px"
                    style={{
                      border: "1px solid rgba(0,0,0,0.5)",
                      height: "60px",
                    }}
                  />
                </InputContainer>
                <Button
                  variant="primary1"
                  textAlign="center"
                  fontFamily="var(--mori)"
                  btnPadding="12px 40px 12px 48px"
                  borderRadius="50px"
                  fontSize="14px"
                  style={{
                    backgroundColor: "#282828",
                    marginTop: "24px",
                    height: "60px",
                  }}
                  btnTextStyles={{
                    style: {
                      gap: "8px",
                      display: "flex",
                      "align-items": "center",
                    },
                  }}
                  // width="1"
                  onClick={onSubmitButtonClick}
                >
                  Subscribe
                  <StyledSvgSprite
                    icon="icon-arrow-up-right"
                    height="20px"
                    width="20px"
                    fill={theme.colors.white}
                  />
                </Button>
              </FormContent>

              <FormMessage variant="heading4" fontSize="16px" lineHeight="24px">
                Here's to building healthier lives, one newsletter at a time.
              </FormMessage>
            </>
          )}
        </FormCard>
      </RightSectionWrapper>
    </RightSection>
  );
}

export default RightSideView;
